<template>
    <div class="chat-widget">
        <transition name="fade">
            <b-card v-if="open" no-body class="chat-widget-panel shadow">
                <b-card-header class="bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                    <template v-if="mode === 'thread'">
                        <b-button variant="link" size="sm" class="text-white p-0" @click="backToList">
                            <b-icon icon="arrow-left"></b-icon>
                        </b-button>
                        <strong class="text-truncate mx-2">{{ activeUser }}</strong>
                        <b-button variant="link" size="sm" class="text-white p-0" @click="closeTicket" title="Cerrar Ticket">
                            <b-icon icon="check-circle"></b-icon>
                        </b-button>
                    </template>
                    <template v-else>
                        <strong>Tickets Abiertos</strong>
                        <b-button variant="link" size="sm" class="text-white p-0" to="/admin/support/open">Ver todos</b-button>
                    </template>
                </b-card-header>

                <b-card-body class="chat-widget-messages p-0" v-if="mode === 'list'">
                    <b-list-group flush v-if="items.length">
                        <b-list-group-item
                            v-for="item in items"
                            :key="item.id"
                            button
                            @click="openThread(item)"
                            class="py-2">
                            <div class="d-flex justify-content-between align-items-center">
                                <strong class="small text-truncate">{{ item.user_name }}</strong>
                                <b-badge pill :variant="roleVariant(item.user_role)">{{ roleLabel(item.user_role) }}</b-badge>
                            </div>
                            <p class="mb-1 small text-truncate">{{ item.reason }}</p>
                            <div class="d-flex justify-content-between align-items-center">
                                <small class="text-muted">
                                    {{ item.last_admin_reply ? 'Atendido por ' + item.last_admin_reply : 'Sin responder' }}
                                </small>
                                <small class="text-muted">{{ item.date }}</small>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                    <p v-else class="text-muted text-center small my-4 mx-3">No hay tickets abiertos por el momento.</p>
                </b-card-body>

                <b-card-body class="chat-widget-messages p-3" v-else v-chat-scroll>
                    <template v-for="(msg, idx) in messages">
                        <b-row :key="idx" v-if="msg.user_id === user.id" align-h="end" class="mx-0 mb-2">
                            <b-col cols="auto" class="pl-4 px-0">
                                <b-list-group-item variant="primary" class="rad-right text-right">
                                    <a v-if="msg.attachment" :href="'/' + msg.attachment" target="_blank">
                                        <img :src="'/' + msg.attachment" class="chat-widget-attachment"/>
                                    </a>
                                    <p class="mb-0" v-if="msg.message">{{ msg.message }}</p>
                                    <small>{{ msg.date }}</small>
                                </b-list-group-item>
                            </b-col>
                        </b-row>
                        <b-row :key="idx" v-else align-h="start" class="mx-0 mb-2">
                            <b-col cols="auto" class="pr-4 px-0">
                                <b-list-group-item variant="secondary" class="rad-left">
                                    <h6 class="mb-1">
                                        {{ msg.name }}
                                        <b-badge pill :variant="roleVariant(msg.role)">{{ roleLabel(msg.role) }}</b-badge>
                                    </h6>
                                    <a v-if="msg.attachment" :href="'/' + msg.attachment" target="_blank">
                                        <img :src="'/' + msg.attachment" class="chat-widget-attachment"/>
                                    </a>
                                    <p class="mb-0" v-if="msg.message">{{ msg.message }}</p>
                                    <small>{{ msg.date }}</small>
                                </b-list-group-item>
                            </b-col>
                        </b-row>
                    </template>
                </b-card-body>

                <b-card-footer class="p-2" v-if="mode === 'thread'">
                    <div class="mb-2 small d-flex align-items-center" v-if="file">
                        <b-icon icon="image" class="mr-1"></b-icon>
                        <span class="text-truncate flex-grow-1">{{ file.name }}</span>
                        <b-button variant="link" size="sm" class="p-0 ml-1" @click="clearFile">
                            <b-icon icon="x-circle-fill"></b-icon>
                        </b-button>
                    </div>
                    <b-form @submit.stop.prevent="send">
                        <b-input-group>
                            <b-input-group-prepend>
                                <b-button variant="outline-secondary" :disabled="loading" @click="$refs.fileInput.click()">
                                    <b-icon icon="paperclip"></b-icon>
                                </b-button>
                            </b-input-group-prepend>
                            <b-form-input
                                v-model="form.msg"
                                placeholder="Escribe tu respuesta..."
                                :disabled="loading"
                                autocomplete="off"
                            ></b-form-input>
                            <b-input-group-append>
                                <b-button type="submit" variant="primary" :disabled="loading || (!form.msg && !file)">
                                    <b-spinner small type="grow" v-if="loading"></b-spinner>
                                    <b-icon icon="chat-left-text-fill" v-else></b-icon>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                        <input type="file" ref="fileInput" accept="image/png,image/jpeg" class="d-none" @change="onFile"/>
                    </b-form>
                </b-card-footer>
            </b-card>
        </transition>

        <b-button variant="primary" pill class="chat-widget-btn shadow" @click="toggle">
            <i class="material-icons">{{ open ? 'close' : 'support_agent' }}</i>
            <b-badge v-if="items.length && !open" variant="danger" pill class="chat-widget-badge">{{ items.length < 10 ? items.length : '+9' }}</b-badge>
        </b-button>
    </div>
</template>

<script>
import axios from "axios";

const ROLE_LABELS = {
    Customer: 'Cliente',
    Partner: 'Agente',
    Supervisor: 'Supervisor',
    Cashier: 'Cajero',
    Administrator: 'Admin',
    Guest: 'Invitado'
};
const ROLE_VARIANTS = {
    Customer: 'info',
    Partner: 'primary',
    Supervisor: 'warning',
    Cashier: 'secondary',
    Administrator: 'dark',
    Guest: 'success'
};

export default {
    name: "AdminChatWidget",
    data: () => ({
        open: false,
        mode: 'list',
        items: [],
        knownCount: null,
        activeId: null,
        activeUser: '',
        messages: [],
        file: null,
        form: {
            msg: ''
        },
        loading: false,
        timeout: null,
        titleTimer: null,
        originalTitle: null
    }),
    computed: {
        user() {
            return this.$store.getters.user;
        }
    },
    mounted() {
        this.poll();
    },
    beforeDestroy() {
        clearTimeout(this.timeout);
        this.stopAlert();
    },
    methods: {
        roleLabel(role) {
            return ROLE_LABELS[role] || role;
        },
        roleVariant(role) {
            return ROLE_VARIANTS[role] || 'secondary';
        },
        toggle() {
            this.open = !this.open;
            if (this.open) {
                this.mode === 'thread' ? this.fetchMessages() : this.fetchList();
                this.stopAlert();
            }
        },
        openThread(item) {
            this.mode = 'thread';
            this.activeId = item.id;
            this.activeUser = item.user_name;
            this.messages = [];
            this.fetchMessages();
        },
        backToList() {
            this.mode = 'list';
            this.activeId = null;
            this.messages = [];
            this.fetchList();
        },
        fetchList() {
            axios.get('/api/admin/tickets/open').then((res) => {
                if (res.data.status === 'success') {
                    this.items = res.data.tickets;
                    if (this.knownCount !== null && this.items.length > this.knownCount) {
                        this.notify();
                    }
                    this.knownCount = this.items.length;
                }
            }).catch(() => {});
        },
        fetchMessages() {
            if (!this.activeId) { return; }
            axios.get('/api/customer/tickets/' + this.activeId + '/messages').then((res) => {
                if (res.data.status === 'success') {
                    this.messages = res.data.messages;
                }
            }).catch(() => {});
        },
        poll() {
            if (this.open && this.mode === 'thread') {
                this.fetchMessages();
                this.timeout = setTimeout(this.poll, 5000);
            } else {
                this.fetchList();
                this.timeout = setTimeout(this.poll, this.open ? 15000 : 30000);
            }
        },
        onFile(e) {
            this.file = e.target.files.length ? e.target.files[0] : null;
        },
        clearFile() {
            this.file = null;
            this.$refs.fileInput.value = '';
        },
        send() {
            if ((!this.form.msg && !this.file) || !this.activeId) { return; }
            this.loading = true;

            let payload;
            if (this.file) {
                payload = new FormData();
                payload.append('msg', this.form.msg);
                payload.append('image', this.file);
            } else {
                payload = this.form;
            }

            axios.post('/api/customer/tickets/' + this.activeId + '/messages/send', payload).then((res) => {
                if (res.data.status === 'success') {
                    this.form.msg = '';
                    this.clearFile();
                    this.fetchMessages();
                } else {
                    this.$root.$bvToast.toast(res.data.msg, {
                        title: 'Notificación',
                        headerClass: 'd-none',
                        variant: res.data.status,
                        toaster: 'b-toaster-top-right',
                        autoHideDelay: 5000,
                        solid: true,
                        appendToast: true
                    });
                }
            }).catch(() => {
                this.$root.$bvToast.toast('No se pudo enviar el mensaje, intenta nuevamente.', {
                    title: 'Notificación',
                    headerClass: 'd-none',
                    variant: 'error',
                    toaster: 'b-toaster-top-right',
                    autoHideDelay: 5000,
                    solid: true,
                    appendToast: true
                });
            }).then(() => {
                this.loading = false;
            });
        },
        closeTicket() {
            this.$swal({
                title: '¿Estas seguro de cerrar este ticket?',
                text: 'Ticket #' + this.activeId,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Si, cerrar',
                confirmButtonColor: '#28a745',
                cancelButtonText: 'No, dejar abierto',
                cancelButtonColor: '#dc3545',
                allowOutsideClick: false,
                allowEscapeKey: false,
                reverseButtons: true
            }).then((result) => {
                if (!result.value) { return; }

                let formData = new FormData();
                formData.append('id', this.activeId);

                axios.post('/api/customer/tickets/close', formData).then((res) => {
                    this.$root.$bvToast.toast(res.data.msg, {
                        title: 'Notificación',
                        headerClass: 'd-none',
                        variant: res.data.status,
                        toaster: 'b-toaster-top-right',
                        autoHideDelay: 5000,
                        solid: true,
                        appendToast: true
                    });
                    if (res.data.status === 'success') {
                        this.backToList();
                    }
                }).catch(() => {});
            });
        },
        notify() {
            try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.frequency.value = 880;
                osc.connect(gain);
                gain.connect(ctx.destination);
                gain.gain.setValueAtTime(0.15, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
                osc.start();
                osc.stop(ctx.currentTime + 0.3);
            } catch (e) {}

            if (this.titleTimer || document.hasFocus()) { return; }
            this.originalTitle = document.title;
            let toggled = false;
            this.titleTimer = setInterval(() => {
                document.title = toggled ? this.originalTitle : '💬 Nuevo ticket';
                toggled = !toggled;
            }, 1000);
            window.addEventListener('focus', this.stopAlert);
        },
        stopAlert() {
            if (!this.titleTimer) { return; }
            clearInterval(this.titleTimer);
            this.titleTimer = null;
            if (this.originalTitle) { document.title = this.originalTitle; }
            window.removeEventListener('focus', this.stopAlert);
        }
    }
}
</script>

<style scoped>
.chat-widget {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1050;
}
.chat-widget-btn {
    width: 56px;
    height: 56px;
    padding: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}
.chat-widget-btn .material-icons {
    font-size: 26px;
    line-height: 1;
}
.chat-widget-badge {
    position: absolute;
    top: -4px;
    right: -4px;
}
.chat-widget-panel {
    position: absolute;
    right: 0;
    bottom: 68px;
    width: 320px;
    max-width: calc(100vw - 32px);
}
.chat-widget-messages {
    height: 360px;
    overflow-y: auto;
    overflow-x: hidden;
}
.chat-widget-attachment {
    max-width: 100%;
    max-height: 160px;
    border-radius: 6px;
    display: block;
    margin-bottom: 4px;
}
.rad-left {
    border-radius: 8px !important;
    border-top-left-radius: 0 !important;
}
.rad-right {
    border-radius: 8px !important;
    border-top-right-radius: 0 !important;
}
small, .small {
    font-size: 60%;
}
.fade-enter-active, .fade-leave-active {
    transition: opacity .15s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
